/**
 * Token Invalidation Service
 * Resolves CWE-613: Insufficient Session Expiration
 * Implements proper token invalidation and blacklisting mechanisms
 */

import { fetchApi } from './api';

class TokenInvalidationService {
  constructor() {
    this.invalidatedTokens = new Set();
    this.tokenBlacklist = new Set();
  }

  /**
   * Invalidate token on server-side and add to blacklist
   * @param {string} token - The access token to invalidate
   * @param {string} userEmail - User email for audit logging
   * @returns {Promise<boolean>} - Success status
   */
  async invalidateToken(token, userEmail) {
    if (!token) {
      console.warn('[TokenInvalidation] No token provided for invalidation');
      return false;
    }

    try {
      // 1. Add to client-side blacklist immediately
      this.addToBlacklist(token);

      // 2. Send invalidation request to server (blacklists token in DynamoDB)
      const response = await fetchApi('/api/session/logout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        skipLoader: true
      });

      if (response.success) {
        console.log('[TokenInvalidation] Token successfully invalidated on server');
        return true;
      } else {
        console.error('[TokenInvalidation] Server token invalidation failed:', response.error);
        return false;
      }
    } catch (error) {
      console.error('[TokenInvalidation] Token invalidation request failed:', error);
      // Keep token in blacklist even if server request fails
      return false;
    }
  }

  /**
   * Invalidate all tokens for a user (multiple device logout)
   * @param {string} userEmail - User email
   * @returns {Promise<boolean>} - Success status
   */
  async invalidateAllUserTokens(userEmail) {
    if (!userEmail) {
      console.warn('[TokenInvalidation] No user email provided');
      return false;
    }

    try {
      const response = await fetchApi('/api/auth/invalidate-all-tokens', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userEmail: userEmail,
          timestamp: new Date().toISOString(),
          reason: 'logout_all_devices'
        }),
        skipLoader: true
      });

      return response.success || false;
    } catch (error) {
      console.error('[TokenInvalidation] Failed to invalidate all user tokens:', error);
      return false;
    }
  }

  /**
   * Add token to client-side blacklist
   * @param {string} token - Token to blacklist
   */
  addToBlacklist(token) {
    if (token) {
      this.tokenBlacklist.add(token);
      this.invalidatedTokens.add(token);
      
      // Store in sessionStorage for persistence across page loads
      try {
        const blacklist = Array.from(this.tokenBlacklist);
        sessionStorage.setItem('token_blacklist', JSON.stringify(blacklist));
      } catch (error) {
        console.warn('[TokenInvalidation] Failed to store blacklist in sessionStorage:', error);
      }
    }
  }

  /**
   * Check if token is in blacklist
   * @param {string} token - Token to check
   * @returns {boolean} - True if token is blacklisted
   */
  isTokenBlacklisted(token) {
    if (!token) return true;
    
    // Load blacklist from sessionStorage if empty
    if (this.tokenBlacklist.size === 0) {
      this.loadBlacklistFromStorage();
    }
    
    return this.tokenBlacklist.has(token) || this.invalidatedTokens.has(token);
  }

  /**
   * Load blacklist from sessionStorage
   */
  loadBlacklistFromStorage() {
    try {
      const storedBlacklist = sessionStorage.getItem('token_blacklist');
      if (storedBlacklist) {
        const blacklist = JSON.parse(storedBlacklist);
        if (Array.isArray(blacklist)) {
          blacklist.forEach(token => this.tokenBlacklist.add(token));
        }
      }
    } catch (error) {
      console.warn('[TokenInvalidation] Failed to load blacklist from sessionStorage:', error);
    }
  }

  /**
   * Verify token validity with server
   * @param {string} token - Token to verify
   * @returns {Promise<boolean>} - True if token is valid
   */
  async verifyTokenValidity(token) {
    if (!token || this.isTokenBlacklisted(token)) {
      return false;
    }

    try {
      const response = await fetchApi('/api/auth/verify-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ token }),
        skipLoader: true
      });

      if (!response.valid) {
        // Token is invalid on server, add to blacklist
        this.addToBlacklist(token);
        return false;
      }

      return true;
    } catch (error) {
      console.error('[TokenInvalidation] Token verification failed:', error);
      // Assume invalid on network error for security
      this.addToBlacklist(token);
      return false;
    }
  }

  /**
   * Clear all invalidated tokens and blacklist
   */
  clearBlacklist() {
    this.tokenBlacklist.clear();
    this.invalidatedTokens.clear();
    try {
      sessionStorage.removeItem('token_blacklist');
    } catch (error) {
      console.warn('[TokenInvalidation] Failed to clear blacklist from sessionStorage:', error);
    }
  }

  /**
   * Get blacklist size for monitoring
   * @returns {number} - Number of blacklisted tokens
   */
  getBlacklistSize() {
    return this.tokenBlacklist.size;
  }

  /**
   * Complete logout with proper token invalidation
   * @param {Object} msalInstance - MSAL instance for Azure AD logout
   * @param {string} token - Current access token
   * @param {string} userEmail - User email
   * @returns {Promise<void>}
   */
  async secureLogout(msalInstance, token, userEmail) {
    try {
      // 1. Invalidate current token
      if (token) {
        await this.invalidateToken(token, userEmail);
      }

      // 2. Clear all local storage
      this.clearAllLocalStorage();

      // 3. Handle MSAL logout based on user type
      const isSupplier = sessionStorage.getItem('user_type') === 'supplier';
      
      if (isSupplier) {
        // Supplier logout - clear everything and redirect
        window.location.href = '/login';
      } else if (msalInstance) {
        // Azure AD logout
        await msalInstance.logoutRedirect({
          postLogoutRedirectUri: process.env.REACT_APP_SSO_LOGOUT_REDIRECT_URI || window.location.origin + '/'
        });
      } else {
        // Fallback logout
        window.location.href = '/login';
      }

    } catch (error) {
      console.error('[TokenInvalidation] Secure logout failed:', error);
      // Force logout even if token invalidation fails
      this.clearAllLocalStorage();
      window.location.href = '/login';
    }
  }

  /**
   * Clear all local storage data securely
   */
  clearAllLocalStorage() {
    try {
      // Clear specific auth-related items first
      const authKeys = [
        'userEmail', 'userToken', 'userName', 'supplierEmail', 'user_type',
        'app_session_id', 'token_blacklist'
      ];
      
      authKeys.forEach(key => {
        sessionStorage.removeItem(key);
        localStorage.removeItem(key);
      });

      // Clear all MSAL cache
      const allSessionKeys = Object.keys(sessionStorage);
      allSessionKeys.forEach(key => {
        if (key.includes('msal') || key.includes('b2c')) {
          sessionStorage.removeItem(key);
        }
      });

      const allLocalKeys = Object.keys(localStorage);
      allLocalKeys.forEach(key => {
        if (key.includes('msal') || key.includes('b2c')) {
          localStorage.removeItem(key);
        }
      });

      // Final complete clear
      sessionStorage.clear();
      localStorage.clear();

      console.log('[TokenInvalidation] All local storage cleared');
    } catch (error) {
      console.error('[TokenInvalidation] Failed to clear local storage:', error);
    }
  }
}

// Export singleton instance
export const tokenInvalidationService = new TokenInvalidationService();
export default tokenInvalidationService;